import type { BudgetStatus } from '../domain/types';
import { el } from './dom';

/**
 * The slim notices that sit under the masthead, above whatever screen is showing.
 *
 * Deliberately banners rather than modals: they should inform without interrupting a
 * search that just succeeded.
 */

/**
 * The one shared login, until per-person accounts exist. Named rather than flagged on
 * the session because nothing else distinguishes it - and when individual accounts
 * land, dropping `guest` from `AUTH_USERS` retires the banner with it.
 */
const SHARED_ACCOUNT = 'guest';

let noticeDismissed = false;

/**
 * Asks whoever is on the shared login not to pass it around.
 *
 * Dismissible, and only for the rest of the page load: it is a standing request rather
 * than a state that clears, so it should be waved away once read and said again next
 * visit - which is also when someone new might be the one reading it.
 */
export function sharedAccountBanner(
  user: string | null,
  onChange: () => void,
): HTMLElement | null {
  if (user !== SHARED_ACCOUNT || noticeDismissed) return null;

  return el(
    'div',
    { class: 'banner banner--notice', role: 'status' },
    el(
      'div',
      {},
      el('strong', {}, 'Please do not share your login with others at this time!'),
      el(
        'p',
        {},
        "I'm trying to figure out if I can stay within the free usage limits. " +
          'Individual user accounts are available if you want your own limits. Thank you!',
      ),
    ),
    el(
      'button',
      {
        class: 'banner__dismiss',
        type: 'button',
        'aria-label': 'Dismiss',
        onclick: () => {
          noticeDismissed = true;
          onChange();
        },
      },
      '×',
    ),
  );
}

/**
 * Surfaces how much of the month's Google budget is left. Only the *global* pool gets
 * scarcity framing - the per-user limits are personal and self-clearing, so they're
 * phrased as "slow down", not "we're running out".
 */

const dismissed = new Set<string>();

function formatReset(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'soon';

  const withinADay = date.getTime() - Date.now() < 24 * 60 * 60 * 1000;
  return withinADay
    ? date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

interface Copy {
  title: string;
  detail: string;
}

function copyFor(budget: BudgetStatus): Copy | null {
  const reset = formatReset(budget.resetsAt);

  if (budget.scope === 'burst') {
    return { title: 'Slow down a sec', detail: `Too many searches at once. Try again at ${reset}.` };
  }

  // The two personal scopes name the account's own numbers. They used to say only
  // "getting close", which left the obvious question - close to what? - unanswered
  // while the footer three lines below had the figures all along.
  if (budget.scope === 'user-daily') {
    return budget.level === 'exhausted'
      ? { title: "That's your searches for today", detail: `Resets ${reset}.` }
      : {
          title: `${budget.remaining} of your ${budget.limit} searches left today`,
          detail: `Resets ${reset}.`,
        };
  }

  if (budget.scope === 'user-monthly') {
    return budget.level === 'exhausted'
      ? {
          title: "That's your searches for this month",
          detail: `Resets ${reset}. Anything already looked up still works.`,
        }
      : {
          title: `${budget.remaining} of your ${budget.limit} searches left this month`,
          detail: `Resets ${reset}.`,
        };
  }

  switch (budget.level) {
    case 'exhausted':
      return {
        title: 'Monthly lookup limit reached',
        detail: `New searches resume ${reset}. Anything already looked up still works.`,
      };
    case 'critical':
    case 'warn':
      return {
        title: `${budget.remaining} of ${budget.limit} monthly lookups left`,
        detail: `Resets ${reset}.`,
      };
    default:
      return null;
  }
}

export function budgetBanner(
  budget: BudgetStatus | null,
  onChange: () => void,
): HTMLElement | null {
  if (!budget || budget.level === 'ok') return null;

  const copy = copyFor(budget);
  if (!copy) return null;

  // `warn` is a nudge and can be waved away; once it's critical it stays put.
  const dismissible = budget.level === 'warn';
  const key = `${budget.scope}:${budget.level}:${budget.resetsAt}`;
  if (dismissible && dismissed.has(key)) return null;

  return el(
    'div',
    { class: `banner banner--${budget.level}`, role: 'status' },
    el('div', {}, el('strong', {}, copy.title), el('p', {}, copy.detail)),
    dismissible &&
      el(
        'button',
        {
          class: 'banner__dismiss',
          type: 'button',
          'aria-label': 'Dismiss',
          onclick: () => {
            dismissed.add(key);
            onChange();
          },
        },
        '×',
      ),
  );
}
